import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";

import {
  sumTonsCo2e,
  splitAggregatedTransactionsBreakdownSubcategories
} from "@aclymatepackages/other-helpers";
import {
  buildScopesRealDataObj,
  buildEmissionGroupData
} from "@aclymatepackages/chart-helpers";

import {
  subcategories,
  otherSubcategoryObj
} from "@aclymatepackages/subcategories";

import {
  useAccountData,
  useCollectionDataListener,
  useMutateCachedCollection
} from "../firebase";
import { fetchOurApi } from "../utils/apiCalls";
import { getAccountId } from "../accountHelpers";
import useDisplayMeasurementSystem from "../hooks/displayMeasurementSystem";
import {
  reshapeRollupsV2MonthDocsToLegacyShape,
  shouldUseRollupsV2Read
} from "./rollupsV2";

import usePlatformLayoutContext from "../contexts/platformLayout";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(quarterOfYear);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

const sumEmissionsTonsBySubcategory = (emissions, subcategory) => {
  const subcategoryEmissions = emissions.filter(
    (emission) => emission.subcategory === subcategory
  );
  return Math.abs(sumTonsCo2e(subcategoryEmissions));
};

const buildSubcategoriesDataObj = (emissionsArray) => {
  return Object.fromEntries(
    [...subcategories, otherSubcategoryObj].map(({ subcategory }) => {
      const emissionsSum = sumEmissionsTonsBySubcategory(
        emissionsArray,
        subcategory
      );
      return [subcategory, emissionsSum];
    })
  );
};

// Phase 3 read-cutover (per-company canary): rollupsV2ReadEnabled is a
// plain boolean on the v3-companies doc, checked client-side the same way
// rollupsV2Enabled already gates the backend's write-time dual-write --
// there's no backend layer in this data flow to check it server-side, since
// this hook talks to Firestore directly. Defaults to false/absent for every
// company until explicitly flipped, so this is inert until intentionally
// canaried.
export const useTransactionsMonthsData = () => {
  const [{ startDate, rollupsV2ReadEnabled }] = useAccountData();
  const { viewMode } = usePlatformLayoutContext() || {};

  const effectiveRollupsV2ReadEnabled = shouldUseRollupsV2Read(
    rollupsV2ReadEnabled,
    viewMode
  );

  const collectionName = effectiveRollupsV2ReadEnabled
    ? "rollups-v2"
    : "all-aggregated-transactions";
  // rollups-v2's monthly bucket docs have no `date` field to range-query on
  // (see rollupWrites.js's schema) -- monthKey is a "YYYY-MM" string, whose
  // lexicographic ordering is already chronological, so a plain string
  // comparison finds the same set of MONTHS the legacy `date >= startDate`
  // query would. It is not the same exact boundary: a startDate that falls
  // partway through a month (e.g. EditStartDateButton's Jan-1-noon values)
  // includes that whole start month under rollups-v2, where legacy's exact
  // timestamp excludes the portion before it. This is intentional, not a
  // gap to close -- month-level precision is the deliberate boundary
  // rollups-v2 supports (PR review, #2037): a company's totals can shift
  // very slightly the moment it's cut over, which is expected and
  // shouldn't be read as a bug on either side.
  //
  // A company with no startDate yet (not asked for at onboarding, only
  // ever set later via the baseline flow) has no boundary to apply here --
  // querying with an undefined bound would either throw (Firestore rejects
  // `undefined` filter values) or, worse, silently resolve to "right now"
  // (dayjs.utc(undefined) is "now", not "no bound"), hiding all history.
  // No startDate means show everything.
  const startDateBoundaryQueries = {
    rollupsV2: [["monthKey", ">=", dayjs.utc(startDate).format("YYYY-MM")]],
    legacy: [["date", ">=", startDate]]
  };
  const queries = startDate
    ? startDateBoundaryQueries[
        effectiveRollupsV2ReadEnabled ? "rollupsV2" : "legacy"
      ]
    : [];

  const [transactionsMonths = [], transactionsMonthsLoading] =
    useCollectionDataListener(collectionName, queries);

  const legacyShapedTransactionsMonths = effectiveRollupsV2ReadEnabled
    ? reshapeRollupsV2MonthDocsToLegacyShape(transactionsMonths)
    : transactionsMonths;

  return [
    splitAggregatedTransactionsBreakdownSubcategories(
      legacyShapedTransactionsMonths
    ),
    transactionsMonthsLoading
  ];
};

// The only client-facing path allowed to create `/transactions` docs --
// firestore.rules denies direct client creates, so every creation flow
// (manual entry, CSV bulk import, survey answers, recurring-close
// materialization) routes through here.
export const useCreateTransactions = () => {
  const mutateCachedCollection = useMutateCachedCollection();

  const createTransactions = async (transactionsToCreate) => {
    const companyId = getAccountId();

    const response = await fetchOurApi({
      path: `/transactions/create-transactions`,
      method: "POST",
      data: {
        companyId,
        transactions: transactionsToCreate
      }
    });

    await mutateCachedCollection("transactions");

    return response || {};
  };

  return createTransactions;
};

export const useUpdateTransactions = () => {
  const { triggerTransactionUpdateCountdown } =
    usePlatformLayoutContext() || {};

  const updateTransactions = async (transactionsToUpdate) => {
    const companyId = getAccountId();

    return await fetchOurApi({
      path: `/transactions/update-transactions`,
      method: "POST",
      data: {
        companyId,
        transactions: transactionsToUpdate
      },
      callback: () => {
        triggerTransactionUpdateCountdown?.();
      }
    });
  };

  return updateTransactions;
};

const findObjectValuesSum = (object) =>
  Object.values(object).reduce((sum, value) => sum + value, 0);

const buildGroupedChartData = ({ groupedEmissions, viewMode }) => {
  const buildRealDataObj =
    viewMode === "subcategories"
      ? buildSubcategoriesDataObj
      : buildScopesRealDataObj;

  return groupedEmissions.map((emissionsArray, idx) => {
    const subcategoriesObj = buildRealDataObj(emissionsArray, [
      ...subcategories,
      otherSubcategoryObj
    ]);

    const buildWarningObj = () => {
      const singleEmissionWarning = emissionsArray.find(
        ({ warning }) => !!warning
      );

      if (!singleEmissionWarning) {
        return null;
      }

      const { warning } = singleEmissionWarning;
      return warning;
    };

    return {
      ...subcategoriesObj,
      warning: buildWarningObj(),
      id: `emissions-chart-group-${idx}`,
      emissionsSumTons: sumTonsCo2e(emissionsArray),
      totalEmissionsSumTons: findObjectValuesSum(subcategoriesObj),
      viewMode
    };
  });
};

// @aclymatepackages/chart-helpers' buildEmissionGroupData seeds
// latestEmissionDate with the startDate it's given and only ever replaces
// it with a *later* real emission date -- so an undefined seed
// (dayjs(undefined) is "now") makes every real, non-future emission
// compare as "not after" it, and latestEmissionDate stays undefined
// through the whole reduce. That undefined then becomes chart labels' own
// anchor, built by counting backward from it. Every direct caller of
// buildEmissionGroupData should route its startDate through this first --
// the epoch fallback is never displayed (none of them return startDate
// from that call back to their own caller), it only exists so any real
// emission date reliably wins that internal comparison.
export const resolveChartBoundaryStartDate = (startDate) =>
  startDate ?? new Date(0);

export const useEmissionsChartData = ({
  emissions,
  viewMode = "subcategories",
  graphPeriod,
  branding,
  isPercentageChart,
  startDate: startDateOverride
}) => {
  const { convertCarbonUnits } = useDisplayMeasurementSystem();
  const [{ startDate: accountStartDate }] = useAccountData();
  const startDate = resolveChartBoundaryStartDate(
    startDateOverride ?? accountStartDate
  );

  const {
    chartLabelsArray,
    scopesArray,
    subcategoriesArray,
    groupedEmissions
  } = buildEmissionGroupData(emissions, graphPeriod, startDate, branding);

  const buildChartData = () => {
    const preliminaryChartData = buildGroupedChartData({
      groupedEmissions,
      viewMode
    });

    const convertChartDataObject = (chartDataObj, converter) =>
      Object.fromEntries(
        Object.entries(chartDataObj).map(([key, value]) => {
          if (typeof value !== "number") {
            return [key, value];
          }
          return [key, converter(value)];
        })
      );

    const labelChartData = (chartData) =>
      chartData.map((data, idx) => ({
        ...data,
        label: chartLabelsArray[idx]
      }));

    if (isPercentageChart) {
      const percentageConvertedChartData = preliminaryChartData.map(
        (chartDataObj) => {
          const objectSubcategoryProperties = Object.keys(chartDataObj).filter(
            (key) =>
              subcategories.find(({ subcategory }) => subcategory === key)
          );
          const objectSubcategoryValues = objectSubcategoryProperties.map(
            (subcategory) => ({
              key: subcategory,
              value: chartDataObj[subcategory]
            })
          );

          const periodEmissionsSum = objectSubcategoryValues.reduce(
            (sum, { value }) => value + sum,
            0
          );

          const percentageConverter = (value) =>
            (value / periodEmissionsSum) * 100;

          const newObject = Object.fromEntries(
            objectSubcategoryValues.map(({ key, value }) => [key, value])
          );

          return convertChartDataObject(newObject, percentageConverter);
        }
      );
      return labelChartData(percentageConvertedChartData);
    }

    const unitConvertedChartData = preliminaryChartData.map((chartDataObj) =>
      convertChartDataObject(chartDataObj, convertCarbonUnits)
    );

    return labelChartData(unitConvertedChartData);
  };

  const chartData = buildChartData();

  const chartArray = viewMode === "scopes" ? scopesArray : subcategoriesArray;

  return { chartData, chartArray };
};

const getMonthKey = (transaction) => {
  const date = new Date(transaction.date);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
};

const buildMonthDate = (monthId) => {
  const [year, month] = monthId.split("-");
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return new Date(Date.UTC(year, month - 1, lastDay)).toISOString();
};

export const aggregateTransactionsByMonth = (transactions) => {
  // A transaction can have a null date (prepareFirebaseObj nulls an
  // unparseable date-keyed string rather than writing it through). new
  // Date(null) is a *valid* 1970-01-01, not Invalid Date, so an unguarded
  // getMonthKey would silently fold it into a phantom "1970-01" bucket
  // that shows up in every chart built from this aggregation.
  const datedTransactions = transactions.filter(
    (transaction) => transaction.date
  );

  const groups = datedTransactions.reduce((acc, transaction) => {
    const groupId = getMonthKey(transaction);
    const groupData = acc[groupId] || {};
    const subcategory = transaction.subcategory;

    const subcategoryGroup = groupData[subcategory] || {
      subcategory,
      transactionsCount: 0,
      tonsCo2e: 0
    };

    return {
      ...acc,
      [groupId]: {
        ...groupData,
        [subcategory]: {
          ...subcategoryGroup,
          transactionsCount: subcategoryGroup.transactionsCount + 1,
          tonsCo2e: subcategoryGroup.tonsCo2e + transaction.tonsCo2e
        }
      }
    };
  }, {});

  return Object.entries(groups).map(([monthId, subcategoryGroups]) => ({
    id: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
    date: buildMonthDate(monthId),
    subcategoriesBreakdown: Object.values(subcategoryGroups)
  }));
};

export const aggregateOfficeTransactions = (transactions) => {
  const getCollectionId = (tx) => tx?.office?.id || "unassigned";
  const collectionGroups = transactions.reduce((acc, transaction) => {
    const collectionId = getCollectionId(transaction);
    const existingTransactions = acc[collectionId] || [];

    return {
      ...acc,
      [collectionId]: [...existingTransactions, transaction]
    };
  }, {});

  return Object.fromEntries(
    Object.entries(collectionGroups).map(([collectionId, txs]) => [
      collectionId,
      aggregateTransactionsByMonth(txs)
    ])
  );
};

export const aggregateEmployeeTransactions = (transactions) => {
  const getEmployeeIds = (tx) => tx?.taggedEmployeesIds || [];
  const employeeGroups = transactions.reduce((acc, transaction) => {
    const employeeIds = getEmployeeIds(transaction);

    return employeeIds.reduce((innerAcc, employeeId) => {
      const existingTransactions = innerAcc[employeeId] || [];

      return {
        ...innerAcc,
        [employeeId]: [...existingTransactions, transaction]
      };
    }, acc);
  }, {});

  return Object.entries(employeeGroups).map(([employeeId, txs]) => ({
    id: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
    individualId: employeeId,
    aggregatedTransactions: aggregateTransactionsByMonth(txs)
  }));
};

export const aggregateVendorTransactions = (transactions, vendors) => {
  const getCollectionId = (tx) => tx?.vendorId || "unassigned";
  const collectionGroups = transactions.reduce((acc, transaction) => {
    const collectionId = getCollectionId(transaction);
    const existingTransactions = acc[collectionId] || [];

    return {
      ...acc,
      [collectionId]: [...existingTransactions, transaction]
    };
  }, {});

  const monthlyAggregatedVendors = Object.fromEntries(
    Object.entries(collectionGroups).map(([collectionId, txs]) => [
      collectionId,
      aggregateTransactionsByMonth(txs)
    ])
  );

  return Object.entries(monthlyAggregatedVendors).map(([key, value]) => {
    const vendorObj = vendors.find(({ id }) => id === key);
    return {
      companyId: vendorObj?.vendorId || key,
      aggregatedTransactions: value
    };
  });
};
